import React from "react";
import { Avatar, createStyles } from "@mantine/core";
import { FiX } from "react-icons/fi";
import { LuPill } from "react-icons/lu";
import { useSelector } from "react-redux";
import moment from "moment";
import { firstLetterCap } from "../../utils";

const useStyles = createStyles(() => ({
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    backdropFilter: "blur(2px)",
    zIndex: 1000,
  },

  panel: {
    position: "fixed",
    top: 8,
    right: 8,
    bottom: 8,
    width: 600,
    maxWidth: "calc(100vw - 16px)",
    background: "var(--app-surface-2)",
    borderRadius: 16,
    zIndex: 1001,
    boxShadow: "0 20px 40px -10px rgba(15,23,42,0.22)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "marPanelIn 220ms ease-out",
  },

  header: {
    width: "100%",
    height: 64,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    borderBottom: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    borderRadius: "16px 16px 0 0",
    boxSizing: "border-box",
  },

  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--app-text)",
    fontFamily: "Inter",
    margin: 0,
  },

  closeBtn: {
    background: "transparent",
    border: 0,
    cursor: "pointer",
    color: "var(--app-text-muted)",
    padding: "4px 8px",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
  },

  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    background: "var(--app-surface-2)",
  },

  bodyInner: {
    padding: "24px 20px",
  },

  clientCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "var(--app-surface)",
    borderRadius: 12,
    border: "1px solid var(--app-border)",
    padding: "12px 16px",
    marginBottom: 16,
  },

  clientName: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },

  clientId: {
    margin: 0,
    fontSize: 12,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },

  medCardOuter: {
    background: "var(--app-surface-2)",
    borderRadius: 16,
    border: "1px solid var(--app-border)",
    padding: "15px 13px",
    marginBottom: 16,
  },

  grayHeaderBox: {
    padding: 8,
    marginBottom: 12,
    borderRadius: 8,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface-2)",
  },

  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
    flexWrap: "wrap",
  },

  pillIconBase: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 6,
    flexShrink: 0,
  },

  pillIconNarcotic: {
    background: "var(--app-tint-red)",
    color: "#B91C1C",
  },

  pillIconNormal: {
    background: "var(--app-tint-amber)",
    color: "#92400E",
  },

  medNameHeader: {
    fontSize: 16,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
    flex: 1,
    minWidth: 0,
    margin: 0,
  },

  narcoticBadge: {
    background: "var(--app-tint-red)",
    color: "#B91C1C",
    fontSize: 12,
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 16,
    fontFamily: "Inter",
    flexShrink: 0,
  },

  prnBadge: {
    background: "var(--app-tint-purple)",
    color: "#6B21A8",
    fontSize: 12,
    fontWeight: 500,
    padding: "2px 8px",
    borderRadius: 16,
    fontFamily: "Inter",
    flexShrink: 0,
  },

  infoBox: {
    padding: 12,
    borderRadius: 8,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    boxShadow: "0 1px 2px 0 rgba(16,24,40,0.05)",
  },

  freqRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 7,
    borderBottom: "1px solid var(--app-border)",
  },

  freqText: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },

  timeText: {
    margin: 0,
    fontSize: 10,
    fontWeight: 400,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },

  remainingRight: {
    textAlign: "right",
  },

  remainingNum: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
    fontVariantNumeric: "tabular-nums",
  },

  remainingLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 400,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },

  medNameRow: {
    marginBlock: 12,
  },

  medNameBody: {
    margin: 0,
    fontSize: 14,
    fontWeight: 500,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },

  medForm: {
    margin: 0,
    fontSize: 10,
    fontWeight: 400,
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
  },

  useInstructBox: {
    background: "var(--app-surface)",
    borderRadius: 8,
    padding: "10px 12px",
    marginTop: 4,
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    border: "1px solid var(--app-border)",
  },

  useInstructCol: {
    flex: "1 1 0",
    minWidth: 180,
  },

  detailLabel: {
    margin: "0 0 4px 0",
    color: "var(--app-text-muted)",
    fontFamily: "Inter",
    fontSize: 11,
    fontWeight: 400,
  },

  detailValue: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text)",
    fontFamily: "Inter",
  },

  detailsGrid: {
    background: "var(--app-surface)",
    borderRadius: 10,
    border: "1px solid var(--app-border)",
    padding: "14px 16px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
}));

const MARMedInfoPanel = ({ row, onClose, slots }) => {
  const { classes, cx } = useStyles();
  const { selectedClient } = useSelector((state) => state.mar);

  if (!row) return null;

  const medName = firstLetterCap(row?.form3?.medicationName) || "-";
  const dosage = row?.form3?.dosage
    ? `${row.form3.dosage}${row.form3.unit || ""}`
    : null;
  const medNameDisplay = dosage ? `${medName} (${dosage})` : medName;
  const formDisplay = [row?.form3?.ingestionMode, row?.form3?.dosageForm]
    .filter(Boolean)
    .join(" ");
  const isNarcotic = row?.form3?.isNarcotic === "Yes";
  const isPRN =
    row?.form3?.medicationType === "PRN" || row?.form3?.frequency === "PRN";
  const frequency = row?.form3?.frequency || null;
  const medicationUse = row?.form3?.medicationUse || null;
  const medicationInstruction = row?.form3?.medicationInstruction || null;
  const allSlotTimes = slots
    ? slots
        .map((s) =>
          s.timeSlot ? moment(s.timeSlot, "hh:mm A").format("h:mm A") : null,
        )
        .filter(Boolean)
        .join(", ")
    : row?.timeSlot
      ? moment(row.timeSlot, "hh:mm A").format("h:mm A")
      : null;

  const timeDisplay = isPRN ? "PRN" : allSlotTimes || null;

  const remaining = row?.medicineRemaining ?? null;
  const total = row?.totalMedicine ?? null;

  const clientName = selectedClient?.name || "-";
  const clientId = selectedClient?.id || selectedClient?._id || null;
  const clientImage = selectedClient?.imageurl || selectedClient?.image || null;
  const pharmacyName = selectedClient?.form2?.pharmacyName || null;

  return (
    <>
      <style>{`
        @keyframes marPanelIn {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Backdrop */}
      <div className={classes.backdrop} onClick={onClose} />

      {/* Panel */}
      <div className={classes.panel}>
        {/* Header */}
        <div className={classes.header}>
          <span className={classes.headerTitle}>Medication</span>
          <button className={classes.closeBtn} onClick={onClose} type="button">
            <FiX size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className={classes.body}>
          <div className={classes.bodyInner}>
            {/* Client card */}
            <div className={classes.clientCard}>
              <Avatar src={clientImage} radius="xl" size={48} />
              <div>
                <p className={classes.clientName}>{clientName}</p>
                {clientId && <p className={classes.clientId}>{String(clientId).slice(0, 6).toLowerCase()}</p>}
              </div>
            </div>

            {/* Med card outer */}
            <div className={classes.medCardOuter}>
              {/* Gray header box */}
              <div className={classes.grayHeaderBox}>
                {/* Name row */}
                <div className={classes.nameRow}>
                  {/* Pill icon */}
                  <span
                    className={cx(classes.pillIconBase, {
                      [classes.pillIconNarcotic]: isNarcotic,
                      [classes.pillIconNormal]: !isNarcotic,
                    })}
                  >
                    <LuPill size={15} />
                  </span>
                  <span className={classes.medNameHeader}>
                    {medNameDisplay}
                  </span>
                  {isNarcotic && (
                    <span className={classes.narcoticBadge}>Narcotic</span>
                  )}
                  {isPRN && <span className={classes.prnBadge}>PRN</span>}
                </div>

                {/* Inner white info box */}
                <div className={classes.infoBox}>
                  {/* Freq / remaining row */}
                  <div className={classes.freqRow}>
                    <div>
                      {frequency && (
                        <p className={classes.freqText}>{frequency}</p>
                      )}
                      {timeDisplay && (
                        <p className={classes.timeText}>{timeDisplay}</p>
                      )}
                    </div>
                    {(remaining !== null || total !== null) && (
                      <div className={classes.remainingRight}>
                        <p className={classes.remainingNum}>{remaining ?? "—"}</p>
                        <p className={classes.remainingLabel}>
                          {total ? `of ${total} Remaining` : "—"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Med name + dosage form */}
                  <div className={classes.medNameRow}>
                    <p className={classes.medNameBody}>{medNameDisplay}</p>
                    {formDisplay && (
                      <p className={classes.medForm}>{formDisplay}</p>
                    )}
                  </div>

                  {/* Use + Instruction side-by-side */}
                  {(medicationUse || medicationInstruction) && (
                    <div className={classes.useInstructBox}>
                      {medicationUse && (
                        <div className={classes.useInstructCol}>
                          <p className={classes.detailLabel}>
                            Use of Medication
                          </p>
                          <p className={classes.detailValue}>{medicationUse}</p>
                        </div>
                      )}
                      {medicationInstruction && (
                        <div className={classes.useInstructCol}>
                          <p className={classes.detailLabel}>Instruction</p>
                          <p className={classes.detailValue}>
                            {medicationInstruction}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Details grid */}
              <div className={classes.detailsGrid}>
                {frequency && (
                  <div>
                    <p className={classes.detailLabel}>Frequency</p>
                    <p className={classes.detailValue}>{frequency}</p>
                  </div>
                )}
                {timeDisplay && (
                  <div>
                    <p className={classes.detailLabel}>Schedule</p>
                    <p className={classes.detailValue}>{timeDisplay}</p>
                  </div>
                )}
                {dosage && (
                  <div>
                    <p className={classes.detailLabel}>Strength</p>
                    <p className={classes.detailValue}>{dosage}</p>
                  </div>
                )}
                {row?.form3?.ingestionMode && (
                  <div>
                    <p className={classes.detailLabel}>Route</p>
                    <p className={classes.detailValue}>
                      {row.form3.ingestionMode}
                    </p>
                  </div>
                )}
                {row?.form3?.dosageForm && (
                  <div>
                    <p className={classes.detailLabel}>Form</p>
                    <p className={classes.detailValue}>
                      {row.form3.dosageForm}
                    </p>
                  </div>
                )}
                <div>
                  <p className={classes.detailLabel}>Type</p>
                  <p className={classes.detailValue}>
                    {isPRN ? "PRN" : "Scheduled"}
                  </p>
                </div>
                {pharmacyName && (
                  <div>
                    <p className={classes.detailLabel}>Pharmacy</p>
                    <p className={classes.detailValue}>{pharmacyName}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MARMedInfoPanel;
